
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { listProspects } from "@/api/prospects.api.js";
import {
  listConversationMessages,
  markConversationRead,
  sendConversationMessage
} from "@/api/conversations.api.js";
import { uploadChatMedia, getContentMediaStatus } from "@/api/ghlContent.api.js";
import {
  resumeAllGhlAiConversations,
  resumeGhlAiConversation,
  stopGhlAiConversation
} from "@/api/ghlAi.api.js";
import { buildGhlContactUrl } from "@/utils/ghlAppUrl.js";
import {
  fetchInboxUnreadSummary,
  subscribeInboxEvents
} from "@/utils/inboxUnreadHub.js";
import {
  channelDisplayLabel,
  channelMeta,
  escapeHtml,
  messageKey as buildMessageKey
} from "@/utils/inboxMessageFormat.js";
import ProspectConversationRow from "@/components/prospects/ProspectConversationRow.vue";
import ContactAvatar from "@/components/prospects/ContactAvatar.vue";
import InboxMessageBubble from "@/components/prospects/InboxMessageBubble.vue";

interface ScoreBreakdown {
  fit?: number;
  engagement?: number;
  intent?: number;
  reasons?: string[];
}

interface InboxAttachment {
  url: string;
  mimeType?: string;
  filename?: string;
}

interface InboxMessage {
  id?: number | string | null;
  role: string;
  content: string;
  messageId?: string | null;
  attachments?: InboxAttachment[];
  createdAt?: string | null;
}

interface ChatAttachmentDraft {
  id: number;
  originalFilename: string;
  mimeType: string;
  publicUrl?: string | null;
  status: string;
}

interface Prospect {
  id: string;
  firstName?: string;
  lastName?: string;
  profilePhoto?: string | null;
  email?: string;
  phone?: string;
  source?: string;
  humanHandoff?: boolean;
  aiEnabled?: boolean;
  conversationSummary?: string | null;
  summaryGeneratedAt?: string | null;
  conversationId?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
  lastMessageRole?: string | null;
  messageChannel?: string | null;
  socialPlatform?: string | null;
  socialAccountId?: string | null;
  socialAccountName?: string | null;
  unreadCount?: number;
  leadScore?: number;
  leadGrade?: string;
  scoreBreakdown?: ScoreBreakdown | null;
  scoredAt?: string | null;
}

type GradeFilter = "" | "hot" | "warm" | "cold";

export default defineComponent({
  name: "prospects-list",
  components: {
    ProspectConversationRow,
    InboxMessageBubble,
    ContactAvatar
  },
  props: {
    embedded: {
      type: Boolean,
      default: true
    },
    hasGhlLocation: {
      type: Boolean,
      default: false
    },
    locationId: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loading: true,
      searching: false,
      syncing: false,
      resumingId: "",
      resumingAll: false,
      stoppingId: "",
      loadError: "",
      threadLoading: false,
      threadError: "",
      sendingMessage: false,
      searchQuery: "",
      activeSearch: "",
      activeGrade: "" as GradeFilter,
      gradeFilters: [
        { value: "" as GradeFilter, label: "All" },
        { value: "hot" as GradeFilter, label: "Hot" },
        { value: "warm" as GradeFilter, label: "Warm" },
        { value: "cold" as GradeFilter, label: "Cold" }
      ],
      prospects: [] as Prospect[],
      selectedProspectId: "",
      messages: [] as InboxMessage[],
      newMessageText: "",
      pendingAttachment: null as ChatAttachmentDraft | null,
      uploadingAttachment: false,
      unsubscribeInboxEvents: null as (() => void) | null,
      scrollToBottomTimer: null as ReturnType<typeof setTimeout> | null
    };
  },
  computed: {
    selectedProspect(): Prospect | null {
      const selectedId = this.normalizeProspectId(this.selectedProspectId);
      if (!selectedId) {
        return null;
      }
      return this.prospects.find((prospect) => this.normalizeProspectId(prospect.id) === selectedId) || null;
    },
    selectedChannelMeta() {
      return channelMeta(this.selectedProspect || {});
    },
    selectedChannelLabel(): string {
      if (!this.selectedProspect) {
        return "";
      }
      const label = channelDisplayLabel(this.selectedProspect);
      if (!label || label === "Chat") {
        const hasChannel = Boolean(
          this.selectedProspect.messageChannel || this.selectedProspect.socialPlatform
        );
        return hasChannel ? label : "";
      }
      return label;
    },
    hasPausedConversations(): boolean {
      return this.prospects.some((prospect) => !this.isAiActive(prospect));
    },
    emptyStateMessage(): string {
      if (this.activeSearch && this.activeGrade) {
        return "No prospects match your search and score filter.";
      }
      if (this.activeSearch) {
        return "No prospects match your search.";
      }
      if (this.activeGrade) {
        return "No prospects match this score filter.";
      }
      return "No prospects yet.";
    },
    canSendMessage(): boolean {
      const hasText = Boolean(this.newMessageText.trim());
      const hasReadyAttachment = this.pendingAttachment?.status === "ready";
      return hasText || hasReadyAttachment;
    }
  },
  watch: {
    hasGhlLocation: {
      immediate: false,
      handler(value: boolean) {
        if (value) {
          this.bootstrapInbox();
        }
      }
    }
  },
  mounted() {
    if (this.hasGhlLocation) {
      this.bootstrapInbox();
    } else {
      this.loading = false;
    }
  },
  beforeUnmount() {
    if (this.unsubscribeInboxEvents) {
      this.unsubscribeInboxEvents();
      this.unsubscribeInboxEvents = null;
    }
    if (this.scrollToBottomTimer) {
      clearTimeout(this.scrollToBottomTimer);
      this.scrollToBottomTimer = null;
    }
  },
  methods: {
    normalizeProspectId(id: string | null | undefined) {
      return String(id ?? "").trim();
    },
    prospectIndexById(contactId: string) {
      const normalized = this.normalizeProspectId(contactId);
      if (!normalized) {
        return -1;
      }
      return this.prospects.findIndex(
        (prospect) => this.normalizeProspectId(prospect.id) === normalized
      );
    },
    isProspectSelected(prospect: Prospect) {
      const selectedId = this.normalizeProspectId(this.selectedProspectId);
      return Boolean(selectedId) && this.normalizeProspectId(prospect.id) === selectedId;
    },
    messageKey(message: InboxMessage) {
      return buildMessageKey(message);
    },
    isAiActive(prospect: Prospect) {
      return !prospect.humanHandoff && prospect.aiEnabled !== false;
    },
    formatName(prospect: Prospect) {
      const name = [prospect.firstName, prospect.lastName].filter(Boolean).join(" ").trim();
      return name || "Unknown";
    },
    ghlContactUrl(prospect: Prospect) {
      return buildGhlContactUrl(this.locationId, prospect.id);
    },
    hasSummary(prospect: Prospect) {
      return Boolean(prospect.conversationSummary?.trim());
    },
    formatGrade(grade?: string) {
      const value = String(grade || "cold").toLowerCase();
      if (value === "hot") return "Hot";
      if (value === "warm") return "Warm";
      return "Cold";
    },
    gradeBadgeClass(grade?: string) {
      const value = String(grade || "cold").toLowerCase();
      if (value === "hot") return "badge-light-danger";
      if (value === "warm") return "badge-light-warning";
      return "badge-light-secondary";
    },
    scoreTooltip(prospect: Prospect) {
      const reasons = prospect.scoreBreakdown?.reasons || [];
      if (!reasons.length) {
        return "No score breakdown yet.";
      }
      return reasons.join(", ");
    },
    async bootstrapInbox() {
      if (!this.unsubscribeInboxEvents) {
        this.unsubscribeInboxEvents = subscribeInboxEvents((payload) =>
          this.handleSocketEvent(payload)
        );
      }
      await this.loadProspects();
    },
    handleSocketEvent(payload: Record<string, unknown>) {
      if (payload?.type === "unread" && payload.contactId) {
        this.applyUnreadUpdate(payload);
        return;
      }

      if (payload?.type !== "message" || !payload.contactId) {
        return;
      }

      const contactId = this.normalizeProspectId(String(payload.contactId));
      if (!contactId) {
        return;
      }
      const prospect = this.prospects.find(
        (item) => this.normalizeProspectId(item.id) === contactId
      );
      const incomingMessage = payload.message as InboxMessage | null;

      if (prospect) {
        if (incomingMessage?.content) {
          prospect.lastMessage = incomingMessage.content;
          prospect.lastMessageAt = incomingMessage.createdAt || String(payload.lastMessageAt || "");
          prospect.lastMessageRole = incomingMessage.role;
        } else if (payload.preview) {
          prospect.lastMessage = String(payload.preview);
          prospect.lastMessageAt = String(payload.lastMessageAt || prospect.lastMessageAt || "");
        }

        if (payload.humanHandoff != null) {
          prospect.humanHandoff = Boolean(payload.humanHandoff);
        }
        if (payload.aiEnabled != null) {
          prospect.aiEnabled = Boolean(payload.aiEnabled);
        }
        if (payload.messageChannel) {
          prospect.messageChannel = String(payload.messageChannel);
        }
        if (payload.socialPlatform) {
          prospect.socialPlatform = String(payload.socialPlatform);
        }
        if (payload.socialAccountId) {
          prospect.socialAccountId = String(payload.socialAccountId);
        }
        if (payload.socialAccountName) {
          prospect.socialAccountName = String(payload.socialAccountName);
        }

        this.reorderProspects(contactId);
      }

      if (contactId === this.normalizeProspectId(this.selectedProspectId) && incomingMessage) {
        this.appendMessage(incomingMessage);
        this.scrollToBottom();
      }
    },
    applyUnreadUpdate(payload: Record<string, unknown>) {
      const contactId = this.normalizeProspectId(String(payload.contactId));
      if (!contactId) {
        return;
      }

      const unreadCount = Math.max(0, Number(payload.unreadCount) || 0);
      const prospect = this.prospects.find(
        (item) => this.normalizeProspectId(item.id) === contactId
      );
      if (prospect) {
        prospect.unreadCount = unreadCount;
      }
    },
    async markProspectRead(contactId: string) {
      const id = this.normalizeProspectId(contactId);
      if (!id) {
        return;
      }

      try {
        await markConversationRead(id);
      } catch (_err) {
        return;
      }

      const prospect = this.prospects.find(
        (item) => this.normalizeProspectId(item.id) === id
      );
      if (prospect) {
        prospect.unreadCount = 0;
      }
      fetchInboxUnreadSummary();
    },
    reorderProspects(contactId: string) {
      const id = this.normalizeProspectId(contactId);
      const selectedId = this.normalizeProspectId(this.selectedProspectId);
      if (!id || id === selectedId) {
        return;
      }

      const from = this.prospectIndexById(id);
      if (from < 0) {
        return;
      }

      let target = 0;
      if (selectedId) {
        const selectedIndex = this.prospectIndexById(selectedId);
        if (selectedIndex < 0) {
          return;
        }
        target = selectedIndex + 1;
      }

      if (from === target) {
        return;
      }

      const [prospect] = this.prospects.splice(from, 1);
      const insertAt = from < target ? target - 1 : target;
      this.prospects.splice(insertAt, 0, prospect);
    },
    appendMessage(message: InboxMessage) {
      const key = this.messageKey(message);
      const existingIndex = this.messages.findIndex((item) => this.messageKey(item) === key);
      if (existingIndex >= 0) {
        this.messages[existingIndex] = {
          ...this.messages[existingIndex],
          ...message
        };
        return;
      }
      this.messages.push(message);
    },
    onThreadContentLoaded() {
      if (this.scrollToBottomTimer) {
        clearTimeout(this.scrollToBottomTimer);
      }
      this.scrollToBottomTimer = setTimeout(() => {
        this.scrollToBottomTimer = null;
        this.scrollToBottom();
      }, 50);
    },
    scrollToBottom() {
      const scroll = () => {
        const end = this.$refs.threadEndRef as HTMLElement | undefined;
        if (end) {
          end.scrollIntoView({ block: "end", behavior: "auto" });
        }
        const container = this.$refs.messagesRef as HTMLElement | undefined;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      };

      this.$nextTick(() => {
        scroll();
        this.$nextTick(scroll);
        requestAnimationFrame(() => {
          scroll();
          requestAnimationFrame(scroll);
        });
        for (const delayMs of [0, 50, 150, 400]) {
          window.setTimeout(scroll, delayMs);
        }
      });
    },
    async selectProspect(prospect: Prospect) {
      const id = this.normalizeProspectId(prospect.id);
      if (id === this.normalizeProspectId(this.selectedProspectId)) {
        return;
      }

      this.selectedProspectId = id;
      this.messages = [];
      this.clearAttachment();
      this.threadError = "";
      this.newMessageText = "";
      await this.loadThread(id);
      await this.markProspectRead(id);
    },
    async loadThread(contactId: string) {
      this.threadLoading = true;
      this.threadError = "";

      try {
        const response = await listConversationMessages(contactId, { limit: 100 });
        this.messages = response.data?.messages || [];
      } catch (err) {
        this.threadError = err?.response?.data?.message || err?.message || "Unable to load messages.";
      } finally {
        this.threadLoading = false;
        this.scrollToBottom();
      }
    },
    setGradeFilter(grade: GradeFilter) {
      if (this.activeGrade === grade) {
        return;
      }
      this.activeGrade = grade;
      this.loadProspects();
    },
    showSummary(prospect: Prospect) {
      const summary = prospect.conversationSummary?.trim();
      if (!summary) {
        return;
      }

      const generatedAt = prospect.summaryGeneratedAt
        ? new Date(prospect.summaryGeneratedAt).toLocaleString()
        : null;

      Swal.fire({
        title: `Conversation summary — ${this.formatName(prospect)}`,
        html: generatedAt
          ? `<div class="text-muted fs-7 mb-3">Generated ${generatedAt}</div><div class="text-start">${escapeHtml(summary).replace(/\n/g, "<br>")}</div>`
          : `<div class="text-start">${escapeHtml(summary).replace(/\n/g, "<br>")}</div>`,
        confirmButtonText: "Close",
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
    },
    async loadProspects({ sync = false, preserveSelection = true } = {}) {
      if (!this.hasGhlLocation) {
        this.loading = false;
        return;
      }

      const previousSelection = preserveSelection ? this.selectedProspectId : "";
      const isInitialLoad = this.loading;
      if (!isInitialLoad) {
        if (sync) {
          this.syncing = true;
        } else {
          this.searching = true;
        }
      }

      this.loadError = "";

      try {
        const params: Record<string, string | number | boolean> = {
          limit: 50,
          offset: 0
        };
        if (this.activeSearch) {
          params.q = this.activeSearch;
        }
        if (this.activeGrade) {
          params.grade = this.activeGrade;
        }
        if (sync) {
          params.sync = true;
        }

        const response = await listProspects(params);
        this.prospects = response.data?.pageList || [];

        const previousId = this.normalizeProspectId(previousSelection);
        if (previousId && this.prospectIndexById(previousId) >= 0) {
          this.selectedProspectId = previousId;
        } else if (!this.normalizeProspectId(this.selectedProspectId) && this.prospects.length) {
          this.selectedProspectId = this.normalizeProspectId(this.prospects[0].id);
          await this.loadThread(this.selectedProspectId);
          await this.markProspectRead(this.selectedProspectId);
        } else if (
          this.normalizeProspectId(this.selectedProspectId)
          && this.prospectIndexById(this.selectedProspectId) < 0
        ) {
          this.selectedProspectId = this.normalizeProspectId(this.prospects[0]?.id) || "";
          this.messages = [];
          if (this.selectedProspectId) {
            await this.loadThread(this.selectedProspectId);
            await this.markProspectRead(this.selectedProspectId);
          }
        }
      } catch (err) {
        this.loadError = err?.response?.data?.message || err?.message || "Unable to load prospects.";
      } finally {
        this.loading = false;
        this.searching = false;
        this.syncing = false;
      }
    },
    search() {
      this.activeSearch = this.searchQuery.trim();
      this.selectedProspectId = "";
      this.messages = [];
      this.loadProspects({ preserveSelection: false });
    },
    clearSearch() {
      this.searchQuery = "";
      this.activeSearch = "";
      this.selectedProspectId = "";
      this.messages = [];
      this.loadProspects({ preserveSelection: false });
    },
    refreshFromCrm() {
      this.loadProspects({ sync: true });
    },
    openAttachmentPicker() {
      const input = this.$refs.attachmentInputRef as HTMLInputElement | undefined;
      input?.click();
    },
    clearAttachment() {
      this.pendingAttachment = null;
      const input = this.$refs.attachmentInputRef as HTMLInputElement | undefined;
      if (input) {
        input.value = "";
      }
    },
    async handleAttachmentSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      this.uploadingAttachment = true;
      this.pendingAttachment = {
        id: 0,
        originalFilename: file.name,
        mimeType: file.type,
        publicUrl: null,
        status: "pending"
      };

      try {
        const response = await uploadChatMedia(file);
        const uploaded = response.data;
        this.pendingAttachment = {
          id: Number(uploaded.id),
          originalFilename: uploaded.originalFilename || file.name,
          mimeType: uploaded.mimeType || file.type,
          publicUrl: uploaded.publicUrl || null,
          status: uploaded.status || "pending"
        };

        if (this.pendingAttachment.status !== "ready") {
          await this.pollAttachmentReady(this.pendingAttachment.id);
        }
      } catch (err) {
        this.clearAttachment();
        Swal.fire({
          text: (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
            || (err as Error)?.message
            || "Failed to upload attachment.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } finally {
        this.uploadingAttachment = false;
      }
    },
    async pollAttachmentReady(mediaId: number, attempts = 30) {
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        const response = await getContentMediaStatus(mediaId);
        const media = response.data;
        if (media?.status === "ready") {
          this.pendingAttachment = {
            id: Number(media.id),
            originalFilename: media.originalFilename,
            mimeType: media.mimeType,
            publicUrl: media.publicUrl || null,
            status: "ready"
          };
          return;
        }
        if (media?.status === "failed") {
          throw new Error("Attachment upload failed");
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      throw new Error("Attachment upload timed out");
    },
    async sendMessage() {
      const prospect = this.selectedProspect;
      const text = this.newMessageText.trim();
      const mediaIds = this.pendingAttachment?.status === "ready" && this.pendingAttachment.id
        ? [this.pendingAttachment.id]
        : [];

      if (!prospect || (!text && !mediaIds.length) || this.sendingMessage || this.uploadingAttachment) {
        return;
      }

      const optimisticContent = text || this.pendingAttachment?.originalFilename || "Attachment";
      const optimisticMessage: InboxMessage = {
        id: `temp-${Date.now()}`,
        role: "human",
        content: optimisticContent,
        attachments: this.pendingAttachment?.publicUrl
          ? [{
              url: this.pendingAttachment.publicUrl,
              mimeType: this.pendingAttachment.mimeType,
              filename: this.pendingAttachment.originalFilename
            }]
          : [],
        createdAt: new Date().toISOString()
      };

      this.appendMessage(optimisticMessage);
      this.newMessageText = "";
      const attachmentDraft = this.pendingAttachment;
      this.clearAttachment();
      this.sendingMessage = true;
      this.scrollToBottom();

      try {
        const response = await sendConversationMessage(prospect.id, {
          message: text,
          mediaIds
        });
        const saved = response.data?.message as InboxMessage | undefined;
        if (saved) {
          this.messages = this.messages.filter((item) => item !== optimisticMessage);
          this.appendMessage(saved);
        }

        prospect.humanHandoff = Boolean(response.data?.humanHandoff);
        prospect.aiEnabled = response.data?.aiEnabled !== false;
        prospect.lastMessage = saved?.content || optimisticContent;
        prospect.lastMessageAt = saved?.createdAt || optimisticMessage.createdAt || "";
        prospect.lastMessageRole = "human";
        this.reorderProspects(prospect.id);
        this.scrollToBottom();
      } catch (err) {
        this.messages = this.messages.filter((item) => item !== optimisticMessage);
        this.newMessageText = text;
        if (attachmentDraft) {
          this.pendingAttachment = attachmentDraft;
        }
        Swal.fire({
          text: err?.response?.data?.message || err?.message || "Failed to send message.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } finally {
        this.sendingMessage = false;
      }
    },
    async stopAi(prospect: Prospect) {
      if (!this.isAiActive(prospect) || this.stoppingId || this.resumingAll) {
        return;
      }

      this.stoppingId = prospect.id;

      try {
        await stopGhlAiConversation({ contactId: prospect.id });
        prospect.humanHandoff = true;
        prospect.aiEnabled = false;
      } catch (err) {
        Swal.fire({
          text: err?.response?.data?.message || err?.message || "Failed to stop AI for this prospect.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } finally {
        this.stoppingId = "";
      }
    },
    async resumeAllAi() {
      if (this.resumingAll || !this.hasPausedConversations) {
        return;
      }

      const result = await Swal.fire({
        icon: "question",
        title: "Resume AI for every conversation?",
        text: "This turns AI back on for every conversation currently on human handover.",
        showCancelButton: true,
        confirmButtonText: "Resume All",
        cancelButtonText: "Cancel",
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-light"
        }
      });
      if (!result.isConfirmed) {
        return;
      }

      this.resumingAll = true;

      try {
        const response = await resumeAllGhlAiConversations();
        const resumed = Number(response.data?.resumed || 0);
        this.prospects.forEach((prospect) => {
          if (!this.isAiActive(prospect)) {
            prospect.humanHandoff = false;
            prospect.aiEnabled = true;
          }
        });
        Swal.fire({
          text: resumed === 1
            ? "AI is active again for 1 conversation."
            : `AI is active again for ${resumed} conversations.`,
          icon: "success",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } catch (err) {
        Swal.fire({
          text: err?.response?.data?.message || "Failed to resume AI for conversations.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } finally {
        this.resumingAll = false;
      }
    },
    async resumeAi(prospect: Prospect) {
      if (this.isAiActive(prospect) || this.resumingId || this.resumingAll) {
        return;
      }

      this.resumingId = prospect.id;

      try {
        await resumeGhlAiConversation({ contactId: prospect.id });
        prospect.humanHandoff = false;
        prospect.aiEnabled = true;
      } catch (err) {
        Swal.fire({
          text: err?.response?.data?.message || err?.message || "Failed to re-enable AI for this prospect.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } finally {
        this.resumingId = "";
      }
    }
  }
});
